import { Injectable } from '@angular/core'
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http'
import { Observable, of } from 'rxjs'

import { FeatureService } from '@app/services/feature.service'
import { REQUIRES_FEATURE } from '@app/interceptors/feature-gate.context'

/**
 * Single Toggle Point for HTTP feature gating.
 *
 * Mirrors the backend `@requires_feature(...)` decorator: when a request is
 * tagged with `withFeature(name, fallback)` at the call site and the active
 * tenant does NOT have `name` enabled, the request is short-circuited with a
 * synthetic 204 response carrying `fallback` as its body. No network call,
 * no console 404, no provider/service code aware of feature flags.
 *
 * Defense in depth: if a request slips through without a context tag, the
 * backend still returns 404 — this interceptor is the noise-reduction layer.
 */
@Injectable()
export class FeatureGateInterceptor implements HttpInterceptor {
  constructor(private features: FeatureService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const gate = req.context.get(REQUIRES_FEATURE)
    if (gate && !this.features.has(gate.feature)) {
      return of(new HttpResponse({ status: 204, body: gate.fallback }))
    }
    return next.handle(req)
  }
}
